import env from '@config/env';
import { getRefreshTokenPath } from '@utils/authTokens';

import axiosInstance, { UPLOAD_TIMEOUT_MS } from './axios';

const api = {
  get: (url, config = {}) => axiosInstance.get(url, config),
  post: (url, data, config = {}) => axiosInstance.post(url, data, config),
  put: (url, data, config = {}) => axiosInstance.put(url, data, config),
  patch: (url, data, config = {}) => axiosInstance.patch(url, data, config),
  delete: (url, config = {}) => axiosInstance.delete(url, config),
};

const feedApiPrefix = env.APP_TYPE === 'admin' ? 'admin' : 'merchant';

export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: (payload = { deviceId: '' }) =>
    api.post('/auth/logout', payload, { hideErrorToast: true }),
  refreshToken: (refreshToken) => api.post(getRefreshTokenPath(), { refreshToken }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getProfile: () => api.get('/auth/profile'),
  verifyOtp: (payload) => api.post('/auth/verify-otp', payload),
};

export const adminAuthApi = {
  login: (credentials) => api.post('/admin/auth/login', credentials),
  refreshToken: (refreshToken) =>
    api.post(getRefreshTokenPath(), { refreshToken }, { hideErrorToast: true }),
  changePassword: (payload) =>
    api.post('/admin/auth/change-password', payload, { hideErrorToast: true }),
};

export const adminUsersApi = {
  list: (payload) => api.post('/admin/users/list', payload, { hideLoader: true }),
  create: (payload) =>
    api.post('/admin/users/create', payload, { hideLoader: true, hideErrorToast: true }),
  update: (payload) =>
    api.post('/admin/users/update/details', payload, { hideLoader: true, hideErrorToast: true }),
  delete: (payload) =>
    api.post('/admin/users/delete', payload, { hideLoader: true, hideErrorToast: true }),
  updateStatus: (payload) =>
    api.post('/admin/users/status', payload, { hideLoader: true, showSuccessToast: true }),
  updateDetails: (payload) =>
    api.post('/admin/users/update/details', payload, { hideLoader: true, showSuccessToast: true }),
  getProfileDetails: (payload) =>
    api.post(`/${feedApiPrefix}/users/profile/details`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
};

export const adminAppIconApi = {
  list: (payload) =>
    api.post('/admin/app-icon/list', payload, { hideLoader: true, hideErrorToast: true }),
  create: (payload) =>
    api.post('/admin/app-icon/create', payload, { hideLoader: true, hideErrorToast: true }),
  update: (payload) =>
    api.post('/admin/app-icon/update', payload, { hideLoader: true, hideErrorToast: true }),
  delete: (payload) =>
    api.post('/admin/app-icon/delete', payload, { hideLoader: true, hideErrorToast: true }),
};

export const adminAppVersionApi = {
  history: (payload) => api.post('/admin/app-version/history', payload, { hideLoader: true }),
  update: (payload) =>
    api.post('/admin/app-version/update', payload, { hideLoader: true, hideErrorToast: true }),
};

export const adminAnalyticsApi = {
  getDashboard: (payload) =>
    api.post('/admin/analytics/dashboard', payload, { hideLoader: true, hideErrorToast: true }),
};

export const adminQuestApi = {
  list: (payload) => api.post('/admin/quests/list', payload, { hideLoader: true }),
  create: (payload) =>
    api.post('/admin/quests/create', payload, {
      hideLoader: true,
      showSuccessToast: true,
      hideErrorToast: true,
    }),
  delete: (payload) =>
    api.post('/admin/quests/delete', payload, {
      hideLoader: true,
      showSuccessToast: true,
      hideErrorToast: true,
    }),
};

export const adminRestaurantApi = {
  listAll: (payload) => api.post('/admin/restaurant/list/all', payload, { hideLoader: true }),
  listByMerchant: (payload) =>
    api.post('/admin/restaurant/list/by/merchant', payload, { hideLoader: true }),
  getDetail: (payload) => api.post('/admin/restaurant/detail', payload, { hideLoader: true }),
  getDetailWithOwner: (payload) =>
    api.post('/admin/restaurant/detail/with-owner', payload, { hideLoader: true }),
  getDashboard: (payload) => api.post('/admin/restaurant/dashboard', payload, { hideLoader: true }),
  listTeamMembers: (payload) =>
    api.post('/admin/restaurant/team/list', payload, { hideLoader: true }),
  lookupTeamMember: (payload) =>
    api.post('/admin/restaurant/team/lookup', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  addTeamMember: (payload) =>
    api.post('/admin/restaurant/team/add', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getTeamMemberDetail: (payload) =>
    api.post('/admin/restaurant/team/detail', payload, { hideLoader: true }),
  updateTeamMemberPermissions: (payload) =>
    api.post('/admin/restaurant/team/update-permissions', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  removeTeamMember: (payload) =>
    api.post('/admin/restaurant/team/remove', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  listFollowers: (payload) =>
    api.post('/admin/restaurant/followers/list', payload, { hideLoader: true }),
  updateStatus: (payload) =>
    api.post('/admin/restaurant/status', payload, { hideLoader: true, showSuccessToast: true }),
  listClaims: (payload) => api.post('/admin/restaurant/claims/list', payload, { hideLoader: true }),
  getClaimDetail: (payload) =>
    api.post('/admin/restaurant/claims/detail', payload, { hideLoader: true }),
  transferClaim: (payload) =>
    api.post('/admin/restaurant/claims/transfer', payload, {
      hideLoader: true,
      showSuccessToast: true,
      hideErrorToast: true,
    }),
  rejectClaim: (payload) =>
    api.post('/admin/restaurant/claims/reject', payload, {
      hideLoader: true,
      showSuccessToast: true,
      hideErrorToast: true,
    }),
};

export const adminMarketingCollateralApi = {
  list: (payload) =>
    api.post('/admin/marketing-collateral/list', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  upload: (file, config = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/marketing-collateral/upload', formData, {
      hideLoader: true,
      hideErrorToast: true,
      timeout: UPLOAD_TIMEOUT_MS,
      ...config,
    });
  },
  create: (payload) =>
    api.post('/admin/marketing-collateral/create', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  delete: (payload) =>
    api.post('/admin/marketing-collateral/delete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
};

/** Feature categories / sections / subsections (admin CMS → merchant). */
export const adminCategoryApi = {
  listEnums: () =>
    api.post('/admin/launchpad/enums', null, { hideLoader: true, hideErrorToast: true }),
  listCategories: (payload) =>
    api.post('/admin/launchpad/category/list', payload, { hideLoader: true, hideErrorToast: true }),
  getCategory: (payload) =>
    api.post('/admin/category/detail', payload, { hideLoader: true, hideErrorToast: true }),
  createCategory: (payload) =>
    api.post('/admin/launchpad/category/create', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  updateCategory: (payload) =>
    api.post('/admin/category/update', payload, { hideLoader: true, hideErrorToast: true }),
  setCategoryStatus: (payload) =>
    api.post('/admin/category/status', payload, { hideLoader: true, hideErrorToast: true }),
  deleteCategory: (payload) =>
    api.post('/admin/launchpad/category/delete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),

  listSections: (payload) =>
    api.post('/admin/launchpad/section/list', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getSection: (payload) =>
    api.post('/admin/category/section/detail', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  createSection: (payload) =>
    api.post('/admin/launchpad/section/create', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  updateSection: (payload) =>
    api.post('/admin/launchpad/section/update', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  setSectionStatus: (payload) =>
    api.post('/admin/category/section/status', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  deleteSection: (payload) =>
    api.post('/admin/launchpad/section/delete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),

  listSubsections: (payload) =>
    api.post('/admin/launchpad/sub-task/list', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getSubsection: (payload) =>
    api.post('/admin/category/subsection/detail', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  createSubsection: (payload) =>
    api.post('/admin/launchpad/sub-task/create', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  updateSubsection: (payload) =>
    api.post('/admin/launchpad/sub-task/update', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  setSubsectionStatus: (payload) =>
    api.post('/admin/category/subsection/status', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  deleteSubsection: (payload) =>
    api.post('/admin/category/subsection/delete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
};

export const merchantCategoryApi = {
  list: (payload) =>
    api.post('/merchant/category/list', payload, { hideLoader: true, hideErrorToast: true }),
};

export const merchantMarketingCollateralApi = {
  list: (payload) =>
    api.post('/merchant/marketing-collateral/list', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
};

export const adminNudgeApi = {
  listNudges: (payload) =>
    api.post('/admin/nudge/list', payload, { hideLoader: true, hideErrorToast: true }),
  draftNudge: (payload) => api.post('/admin/nudge/draft', payload, { hideErrorToast: true }),
  createNudge: (payload) => api.post('/admin/nudge/create', payload, { hideErrorToast: true }),
  updateNudge: (payload) => api.post('/admin/nudge/update', payload, { hideErrorToast: true }),
  sendNotification: (payload) =>
    api.post('/admin/nudge/send_notification', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  creatifyChat: (payload) =>
    api.post('/admin/nudge/creatify/chat', payload, { hideLoader: true, hideErrorToast: true }),
  creatifyStatus: (payload) =>
    api.post('/admin/nudge/creatify/status', payload, { hideLoader: true, hideErrorToast: true }),
  creatifyHistory: (payload) =>
    api.post('/admin/nudge/creatify/history', payload, { hideLoader: true, hideErrorToast: true }),
  creatifyClear: (payload) =>
    api.post('/admin/nudge/creatify/clear', payload, { hideLoader: true, hideErrorToast: true }),
  getNudgeDetail: (payload) =>
    api.post('/admin/nudge/detail', payload, { hideLoader: true, hideErrorToast: true }),
  getNudgeSocialAccounts: (payload) =>
    api.post('/admin/nudge/social-accounts', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
};

export const adminFeedApi = {
  listFeedPosts: (payload) => api.post('/admin/feed/list', payload, { hideLoader: true }),
  createFeedPost: (payload) =>
    api.post(`/${feedApiPrefix}/feed/create`, payload, { hideLoader: true, hideErrorToast: true }),
  deleteFeedPosts: (payload) =>
    api.post(`/${feedApiPrefix}/feed/delete`, payload, { hideLoader: true, hideErrorToast: true }),
  likeFeedPost: (payload) =>
    api.post(`/${feedApiPrefix}/feed/like`, payload, { hideLoader: true, hideErrorToast: true }),
  listFeedLikeUsers: (payload) =>
    api.post(`/${feedApiPrefix}/feed/like/users`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  listFeedComments: (payload) =>
    api.post(`/${feedApiPrefix}/feed/comment/list`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  addFeedComment: (payload) =>
    api.post(`/${feedApiPrefix}/feed/comment/add`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  deleteFeedComment: (payload) =>
    api.post(`/${feedApiPrefix}/feed/comment/delete`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  uploadFeedPhoto: (files, { restaurantId, ...config } = {}) => {
    const formData = new FormData();
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    list.forEach((file) => formData.append('files', file));
    if (restaurantId) formData.append('restaurantId', restaurantId);
    return api.post('/admin/feed/photo/upload', formData, {
      hideLoader: true,
      hideErrorToast: true,
      ...config,
    });
  },
};

export const merchantAuthApi = {
  sendOtp: (payload) => api.post('/merchant/auth/otp/send', payload, { showSuccessToast: true }),
  resendOtp: (payload) =>
    api.post('/merchant/auth/otp/resend', payload, { showSuccessToast: true }),
  verifyOtp: (payload) => api.post('/merchant/auth/otp/verify', payload),
  sendContactOtp: (payload) =>
    api.post('/merchant/account/contact/otp/send', payload, { showSuccessToast: true }),
  verifyContactOtp: (payload) => api.post('/merchant/account/contact/otp/verify', payload),
  register: (payload, headers = {}) => api.post('/merchant/register', payload, { headers }),
  syncRestaurant: (payload) => api.post('/merchant/restaurant/sync', payload),
  placeAutocomplete: (payload) =>
    api.post('/merchant/restaurant/place/autocomplete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  placeDetail: (payload) =>
    api.post('/merchant/restaurant/place/detail', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  reverseGeocode: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/reverse-geocode`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  listRestaurants: (payload = { search: '' }) =>
    api.post('/merchant/restaurant/list', payload, { hideLoader: true }),
  getRestaurantDetail: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/detail`, payload, { hideLoader: true }),
  updateRestaurant: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/update`, payload, { showSuccessToast: true }),
  removeRestaurantOwnership: (payload) =>
    api.post('/merchant/restaurant/remove_ownership', payload, { showSuccessToast: true }),
  listReviews: (payload) => api.post('/merchant/review/list', payload, { hideLoader: true }),
  getPhotoCategories: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/photos/categories`, payload, { hideLoader: true }),
  listPhotos: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/photos`, payload, { hideLoader: true }),
  listGallery: (payload) =>
    api.post('/merchant/restaurant/gallery/list', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  createGallery: (payload, config = {}) =>
    api.post('/merchant/restaurant/gallery/create', payload, {
      showSuccessToast: true,
      hideLoader: true,
      hideErrorToast: true,
      ...config,
    }),
  deleteGallery: (payload) =>
    api.post('/merchant/restaurant/gallery/delete', payload, {
      showSuccessToast: true,
      hideLoader: true,
      hideErrorToast: true,
    }),
  addPhotos: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/photos/add`, payload, {
      showSuccessToast: true,
      hideLoader: true,
    }),
  deletePhotos: (payload) =>
    api.post(`/${feedApiPrefix}/restaurant/photos/delete`, payload, {
      showSuccessToast: true,
      hideLoader: true,
    }),
  uploadPhoto: (files, { restaurantId, ...config } = {}) => {
    const formData = new FormData();
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    list.forEach((file) => formData.append('files', file));
    if (restaurantId) formData.append('restaurantId', restaurantId);
    return api.post(`/${feedApiPrefix}/restaurant/photos/upload`, formData, {
      hideErrorToast: true,
      ...config,
    });
  },
  listFollowers: (payload) =>
    api.post('/merchant/restaurant/followers/list', payload, { hideLoader: true }),
  getFollowerDetail: (payload) =>
    api.post('/merchant/restaurant/followers/detail', payload, { hideLoader: true }),
  getProfileDetail: (payload) =>
    api.post('/merchant/profile/detail', payload, { hideLoader: true }),
  listFeedPosts: (payload) => api.post('/merchant/feed/list', payload, { hideLoader: true }),
  createFeedPost: (payload) =>
    api.post(`/${feedApiPrefix}/feed/create`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  uploadFeedPhoto: (files, { restaurantId, ...config } = {}) => {
    const formData = new FormData();
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    list.forEach((file) => formData.append('files', file));
    if (restaurantId) formData.append('restaurantId', restaurantId);
    return api.post('/merchant/feed/photo/upload', formData, {
      hideLoader: true,
      hideErrorToast: true,
      ...config,
    });
  },
  likeFeedPost: (payload) =>
    api.post('/merchant/feed/like', payload, { hideLoader: true, hideErrorToast: true }),
  listFeedLikeUsers: (payload) =>
    api.post('/merchant/feed/like/users', payload, { hideLoader: true, hideErrorToast: true }),
  listFeedComments: (payload) =>
    api.post('/merchant/feed/comment/list', payload, { hideLoader: true, hideErrorToast: true }),
  addFeedComment: (payload) =>
    api.post('/merchant/feed/comment/add', payload, { hideLoader: true, hideErrorToast: true }),
  deleteFeedComment: (payload) =>
    api.post('/merchant/feed/comment/delete', payload, { hideLoader: true, hideErrorToast: true }),
  deleteFeedPosts: (payload) =>
    api.post('/merchant/feed/delete', payload, { hideLoader: true, hideErrorToast: true }),
  listTeamMembers: (payload) =>
    api.post('/merchant/restaurant/team/list', payload, { hideLoader: true }),
  lookupTeamMember: (payload) =>
    api.post('/merchant/restaurant/team/lookup', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  addTeamMember: (payload) =>
    api.post('/merchant/restaurant/team/add', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getTeamMemberDetail: (payload) =>
    api.post('/merchant/restaurant/team/detail', payload, { hideLoader: true }),
  updateTeamMemberPermissions: (payload) =>
    api.post('/merchant/restaurant/team/update-permissions', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  removeTeamMember: (payload) =>
    api.post('/merchant/restaurant/team/remove', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getAppConfig: () =>
    api.post('/merchant/app/config', {}, { hideLoader: true, hideErrorToast: true }),
  getRestaurantDashboard: (payload) =>
    api.post('/merchant/restaurant/dashboard', payload, { hideLoader: true }),
  getLaunchpadCategories: (payload) =>
    api.post('/merchant/launchpad/categories', payload, { hideLoader: true }),
  getLaunchpadSections: (payload) =>
    api.post('/merchant/launchpad/sections', payload, { hideLoader: true }),
  completeLaunchpadSection: (payload) =>
    api.post('/merchant/launchpad/section/complete', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  getCrmRedirect: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/crm-redirect`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      skipForbiddenRedirect: true,
      withCredentials: true,
    }),
  getBillingRedirect: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/billing-redirect`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      withCredentials: true,
    }),
  getSocialConnectUrl: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/social/connect-url`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      skipForbiddenRedirect: true,
      withCredentials: true,
    }),
  getSocialPlatforms: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/social/platforms`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      withCredentials: true,
    }),
  getSaasPlans: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/saas/plans`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      withCredentials: true,
    }),
  subscribeSaasPlan: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/saas/subscribe`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      skipForbiddenRedirect: true,
      withCredentials: true,
    }),
  getSocialAccounts: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/social/accounts`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      withCredentials: true,
    }),
  attachSocialAccount: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/social/attach`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      withCredentials: true,
    }),
  disconnectSocialAccount: (payload) =>
    api.post(`/${feedApiPrefix}/ghl/social/disconnect`, payload, {
      hideLoader: true,
      hideErrorToast: true,
      skipForbiddenRedirect: true,
      withCredentials: true,
    }),
  getProfile: () => api.post('/merchant/profile/get', {}, { hideLoader: true }),
  updateProfile: (payload) => api.post('/merchant/profile/update', payload),
  createNudge: (payload) => api.post('/merchant/nudge/create', payload, { hideErrorToast: true }),
  draftNudge: (payload) => api.post('/merchant/nudge/draft', payload, { hideErrorToast: true }),
  updateNudge: (payload) => api.post('/merchant/nudge/update', payload, { hideErrorToast: true }),
  creatifyChat: (payload) =>
    api.post('/merchant/nudge/creatify/chat', payload, { hideLoader: true, hideErrorToast: true }),
  creatifyStatus: (payload) =>
    api.post('/merchant/nudge/creatify/status', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  creatifyHistory: (payload) =>
    api.post('/merchant/nudge/creatify/history', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  creatifyClear: (payload) =>
    api.post('/merchant/nudge/creatify/clear', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  listNudges: (payload) =>
    api.post('/merchant/nudge/list', payload, { hideLoader: true, hideErrorToast: true }),
  getNudgeDetail: (payload) =>
    api.post('/merchant/nudge/detail', payload, { hideLoader: true, hideErrorToast: true }),
  getNudgeSocialAccounts: (payload) =>
    api.post(`/${feedApiPrefix}/nudge/social-accounts`, payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  updateNudgeStatus: (payload) =>
    api.post('/merchant/nudge/update_status', payload, { hideErrorToast: true }),
  sendNudgeNotification: (payload) =>
    api.post('/merchant/nudge/send_notification', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  listNudgeRsvpUsers: (payload) =>
    api.post('/merchant/nudge/rsvps/users', payload, { hideLoader: true, hideErrorToast: true }),
  listNotifications: (payload) =>
    api.post('/merchant/notification/list', payload, { hideLoader: true, hideErrorToast: true }),
  getNotificationUnreadCount: () =>
    api.post(
      '/merchant/notification/unread-count',
      {},
      {
        hideLoader: true,
        hideErrorToast: true,
      },
    ),
  markAllNotificationsAsRead: () =>
    api.post('/merchant/notification/read-all', {}, { hideLoader: true, hideErrorToast: true }),
  updateNotificationReadStatus: (payload) =>
    api.post('/merchant/notification/read-status', payload, {
      hideLoader: true,
      hideErrorToast: true,
    }),
  uploadNudgeMedia: (files, { restaurantId, ...config } = {}) => {
    const formData = new FormData();
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    list.forEach((file) => formData.append('files', file));
    if (restaurantId) formData.append('restaurantId', restaurantId);
    return api.post('/merchant/nudge/photo/upload', formData, {
      hideLoader: true,
      hideErrorToast: true,
      ...config,
    });
  },
};

export const fileApi = {
  upload: (file, config = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/file/upload', formData, { hideErrorToast: true, ...config });
  },
  uploadMultiple: (files, config = {}) => {
    const formData = new FormData();
    (Array.isArray(files) ? files : []).forEach((file) => {
      formData.append('files', file);
    });
    return api.post('/file/upload/multiple', formData, { hideErrorToast: true, ...config });
  },
};

export const onboardingApi = {
  saveProfile: (payload) => api.post('/onboarding/profile', payload),
  savePreferences: (payload) => api.post('/onboarding/preferences', payload),
};

export const userApi = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data, { showSuccessToast: true }),
  update: (id, data) => api.put(`/users/${id}`, data, { showSuccessToast: true }),
  delete: (id) => api.delete(`/users/${id}`, { showSuccessToast: true }),
};

export default api;
